import { Button, Card, EmptyState, Input, Spinner } from '@ohear/ui'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { AdminGuard } from '../../features/admin/AdminGuard'
import { AdminNav } from '../../features/admin/AdminNav'
import { type AuditFilters, useAuditLog } from '../../lib/api/admin'

export const Route = createFileRoute('/admin/audit-log')({
  component: AuditLogPage,
})

const PAGE_SIZE = 50

function AuditLogPage() {
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState<AuditFilters>({})
  const [draft, setDraft] = useState<AuditFilters>({})

  const fullFilters = { page, page_size: PAGE_SIZE, ...filters }
  const log = useAuditLog(fullFilters)

  const items = log.data?.items ?? []
  const total = log.data?.total ?? 0
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const apply = (e: React.FormEvent) => {
    e.preventDefault()
    setFilters({
      organization_id: draft.organization_id || undefined,
      user_id: draft.user_id || undefined,
      action: draft.action || undefined,
      since: draft.since || undefined,
      until: draft.until || undefined,
    })
    setPage(1)
  }

  const clear = () => {
    setDraft({})
    setFilters({})
    setPage(1)
  }

  return (
    <AdminGuard>
      <section className="flex flex-col gap-3" aria-labelledby="admin-audit-title">
        <header className="flex flex-col gap-1">
          <div className="text-[12px] uppercase tracking-wide text-[var(--text-3)]">
            Platform admin
          </div>
          <h1 id="admin-audit-title" className="text-[22px] font-semibold tracking-tight m-0">
            Audit log
          </h1>
          <p className="text-[13px] text-[var(--text-2)] m-0">
            Immutable trail of every action taken on the platform. Filter by org, user, action, or
            time window.
          </p>
        </header>

        <AdminNav />

        <Card>
          <form className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" onSubmit={apply}>
            <label className="flex flex-col gap-1 text-[13px]">
              <span className="font-medium">Organization ID</span>
              <Input
                value={draft.organization_id ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, organization_id: e.target.value }))}
                placeholder="UUID"
              />
            </label>
            <label className="flex flex-col gap-1 text-[13px]">
              <span className="font-medium">User ID</span>
              <Input
                value={draft.user_id ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, user_id: e.target.value }))}
                placeholder="UUID"
              />
            </label>
            <label className="flex flex-col gap-1 text-[13px]">
              <span className="font-medium">Action</span>
              <Input
                value={draft.action ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, action: e.target.value }))}
                placeholder="e.g. admin.org_status_changed"
              />
            </label>
            <label className="flex flex-col gap-1 text-[13px]">
              <span className="font-medium">Since</span>
              <Input
                type="datetime-local"
                value={draft.since ?? ''}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    since: e.target.value ? new Date(e.target.value).toISOString() : undefined,
                  }))
                }
              />
            </label>
            <label className="flex flex-col gap-1 text-[13px]">
              <span className="font-medium">Until</span>
              <Input
                type="datetime-local"
                value={draft.until ?? ''}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    until: e.target.value ? new Date(e.target.value).toISOString() : undefined,
                  }))
                }
              />
            </label>
            <div className="flex items-end gap-2">
              <Button type="submit">Apply</Button>
              <Button type="button" variant="ghost" onClick={clear}>
                Clear
              </Button>
            </div>
          </form>
        </Card>

        <Card dense>
          {log.isLoading ? (
            <div className="flex items-center gap-2 text-[var(--text-2)]">
              <Spinner /> Loading audit log...
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              title="No audit entries"
              description="Try a wider time window or fewer filters."
            />
          ) : (
            <div className="overflow-x-auto -mx-3 -my-3">
              <table
                className="w-full text-[13px] border-separate"
                style={{ borderSpacing: 0 }}
                aria-label="Audit log"
              >
                <thead
                  className="sticky top-0 z-10 text-left text-[11px] uppercase tracking-wide text-[var(--text-2)]"
                  style={{ background: 'var(--bg-3)' }}
                >
                  <tr>
                    <th className="px-3 py-2 font-medium">When</th>
                    <th className="px-3 py-2 font-medium">Action</th>
                    <th className="px-3 py-2 font-medium">Target</th>
                    <th className="px-3 py-2 font-medium">User</th>
                    <th className="px-3 py-2 font-medium">Org</th>
                    <th className="px-3 py-2 font-medium">Metadata</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr key={row.id} className="align-top transition-colors hover:bg-[var(--bg-3)]">
                      <td
                        className="px-3 py-2 text-[var(--text-2)] whitespace-nowrap tabular-nums border-t"
                        style={{ borderColor: 'var(--border)' }}
                      >
                        {new Date(row.timestamp).toLocaleString()}
                      </td>
                      <td
                        className="px-3 py-2 font-mono text-[12px] border-t"
                        style={{ borderColor: 'var(--border)' }}
                      >
                        {row.action}
                      </td>
                      <td
                        className="px-3 py-2 text-[12px] border-t"
                        style={{ borderColor: 'var(--border)' }}
                      >
                        {row.target_type}
                        {row.target_id ? (
                          <span className="text-[var(--text-3)]">
                            {' '}
                            / {row.target_id.slice(0, 8)}
                          </span>
                        ) : null}
                      </td>
                      <td
                        className="px-3 py-2 font-mono text-[12px] text-[var(--text-2)] border-t"
                        style={{ borderColor: 'var(--border)' }}
                      >
                        {row.user_id ? row.user_id.slice(0, 8) : '—'}
                      </td>
                      <td
                        className="px-3 py-2 font-mono text-[12px] text-[var(--text-2)] border-t"
                        style={{ borderColor: 'var(--border)' }}
                      >
                        {row.organization_id.slice(0, 8)}
                      </td>
                      <td
                        className="px-3 py-2 text-[12px] text-[var(--text-2)] max-w-[280px] border-t"
                        style={{ borderColor: 'var(--border)' }}
                      >
                        <code className="break-all whitespace-pre-wrap">
                          {Object.keys(row.metadata).length > 0 ? JSON.stringify(row.metadata) : ''}
                        </code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <div className="flex items-center justify-between text-[12px] text-[var(--text-2)]">
          <span>
            Page {page} of {lastPage} - {total} total
          </span>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="ghost"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={page >= lastPage}
              onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      </section>
    </AdminGuard>
  )
}
